import { ButtonVariant, CoButton } from "components/CoButton";
import Dropdown, { CoDropdownItemProps } from "components/CoDropdown";
import { logDownloadUploadedPaper } from "helpers/services/mixpanel";
import useLabels from "hooks/useLabels";
import { ReactNode } from "react";
import { toast } from "react-toastify";
import useDeleteUploadedPaper from "./hooks/useDeleteUploadedPaper";
import useDownloadUploadedPaper from "./hooks/useDownloadUploadedPaper";

type ManageUploadedPaperDropdownProps = {
  uploaded_paper_id: string;
  isUploading: boolean;
  onDelete?: () => void;
  onOpenChange?: (open: boolean) => void;
  children?: ReactNode;
};

const ManageUploadedPaperDropdown = ({
  uploaded_paper_id,
  isUploading,
  onDelete,
  onOpenChange,
  children,
}: ManageUploadedPaperDropdownProps) => {
  const [labels] = useLabels("research-hub.actions");
  const { deletePaper, isDeleting } = useDeleteUploadedPaper();
  const { downloadPaper, isDownloading } = useDownloadUploadedPaper();

  const handleDownloadPDF = async () => {
    try {
      await downloadPaper(uploaded_paper_id);
      toast.success(labels["download-pdf-success"]);
      logDownloadUploadedPaper(uploaded_paper_id);
    } catch (error) {
      console.error("Error downloading PDF:", error);
    }
  };

  const handleDeletePDF = async () => {
    await deletePaper(uploaded_paper_id);
    toast.info(labels["deleted-paper"]);
    onDelete?.();
  };

  const dropdownItems: CoDropdownItemProps[] = [
    {
      onClick: () => handleDownloadPDF(),
      key: "download-pdf",
      title: isDownloading ? labels["downloading-pdf"] : labels["download-pdf"],
      leftIcon: <i className="icon-download text-xl" />,
    },
    {
      onClick: () => handleDeletePDF(),
      key: "delete-pdf",
      title: isDeleting ? labels["deleting-pdf"] : labels["delete-pdf"],
      leftIcon: <i role="presentation" className="icon-trash-2 text-xl" />,
      danger: true,
    },
  ];

  let disableKeys = [];
  if (isDownloading || isUploading) {
    disableKeys.push("download-pdf");
  }
  if (isDeleting || isUploading) {
    disableKeys.push("delete-pdf");
  }

  return (
    <>
      <Dropdown
        items={dropdownItems}
        disabledKeys={disableKeys}
        onOpenChange={onOpenChange}
      >
        {children ? (
          children
        ) : (
          <CoButton
            size="sm"
            icon="icon-ellipsis-vertical"
            variant={ButtonVariant.Flat}
          />
        )}
      </Dropdown>
    </>
  );
};

export default ManageUploadedPaperDropdown;
